import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaTrash, FaArrowLeft, FaEye, FaEyeSlash, FaSave, FaTimes, FaEnvelope, FaCalendar, FaCrown, FaChartBar, FaDatabase, FaUsers, FaClock, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import api from '../api';
import '../styles/AccountSettings.css';

const AccountSettings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [usernameForm, setUsernameForm] = useState({
    newUsername: '',
    password: ''
  });
  const [showUsernameForm, setShowUsernameForm] = useState(false);

  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    password: ''
  });
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchProfiles();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await api.get('/api/user/profile/');
      setUser(response.data);
      setUsernameForm(prev => ({ ...prev, newUsername: response.data.username }));
      setEmailForm(prev => ({ ...prev, newEmail: response.data.email || '' }));
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load user data');
    }
  };

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/api/profiles/');
      setProfiles(response.data);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      setError('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await api.post('/api/user/change-username/', {
        new_username: usernameForm.newUsername,
        password: usernameForm.password
      });
      setSuccess('Username updated successfully');
      setShowUsernameForm(false);
      setUsernameForm({ newUsername: usernameForm.newUsername, password: '' });
      fetchUserData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update username');
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await api.post('/api/user/change-email/', {
        new_email: emailForm.newEmail,
        password: emailForm.password
      });
      setSuccess('Email updated successfully');
      setShowEmailForm(false);
      setEmailForm({ newEmail: emailForm.newEmail, password: '' });
      fetchUserData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update email');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    try {
      await api.post('/api/user/change-password/', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      setSuccess('Password updated successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowChangePassword(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update password');
    }
  };

  const handleDeleteProfile = async (profileUuid) => {
    if (window.confirm('Are you sure you want to delete this profile? This action cannot be undone.')) {
      try {
        await api.delete(`/api/profiles/${profileUuid}/`);
        setSuccess('Profile deleted successfully');
        fetchProfiles();
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete profile');
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This will permanently delete all your profiles and data. This action cannot be undone.')) {
      try {
        await api.delete('/api/user/delete-account/');
        localStorage.clear();
        navigate('/register');
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete account');
      }
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div className="account-settings">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="account-settings">
      <div className="settings-header">
        <button className="back-button" onClick={() => navigate('/chat')}>
          <FaArrowLeft /> Back to Chat
        </button>
        <h1>Account Settings</h1>
      </div>

      {error && (
        <div className="message error">
          <span>{error}</span>
          <button onClick={clearMessages}><FaTimes /></button>
        </div>
      )}

      {success && (
        <div className="message success">
          <span>{success}</span>
          <button onClick={clearMessages}><FaTimes /></button>
        </div>
      )}

      <div className="settings-content">
        {/* User Information Section */}
        <div className="settings-section user-info-section">
          <h2><FaUser /> User Information</h2>
          
          <div className="user-info-grid">
            {/* Basic Info Card */}
            <div className="info-card basic-info">
              <div className="card-header">
                <FaUser className="card-icon" />
                <h3>Account Details</h3>
              </div>
              <div className="card-content">
                <div className="info-item">
                  <div className="info-label">
                    <FaUser className="info-icon" />
                    <span>Username</span>
                  </div>
                  <div className="info-value">{user?.username}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">
                    <FaEnvelope className="info-icon" />
                    <span>Email</span>
                  </div>
                  <div className="info-value">{user?.email || 'Not provided'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">
                    <FaCalendar className="info-icon" />
                    <span>Member Since</span>
                  </div>
                  <div className="info-value">
                    {user?.date_joined ? new Date(user.date_joined).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Account Tier Card */}
            <div className="info-card tier-info">
              <div className="card-header">
                <FaCrown className="card-icon" />
                <h3>Account Tier</h3>
              </div>
              <div className="card-content">
                <div className="tier-badge-container">
                  <div className={`tier-badge ${user?.account_tier || 'free'}`}>
                    <FaCrown className="tier-icon" />
                    <span className="tier-text">
                      {user?.account_tier ? user.account_tier.charAt(0).toUpperCase() + user.account_tier.slice(1) : 'Free'}
                    </span>
                  </div>
                </div>
                <div className="tier-description">
                  {(user?.account_tier === 'premium' || user?.account_tier === 'pro') && (
                    <>
                      <p>✨ Enjoy unlimited credits and premium features!</p>
                      <button 
                        className="tier-action-button pro"
                        onClick={() => navigate('/subscription')}
                      >
                        Manage Subscription
                      </button>
                    </>
                  )}
                  {user?.account_tier === 'standard' && (
                    <>
                      <p>🚀 Access to enhanced features and increased limits</p>
                      <button 
                        className="tier-action-button upgrade"
                        onClick={() => navigate('/subscription')}
                      >
                        Upgrade to Pro
                      </button>
                    </>
                  )}
                  {(!user?.account_tier || user?.account_tier === 'free') && (
                    <>
                      <p>🌟 Upgrade to unlock more features and higher limits</p>
                      <button 
                        className="tier-action-button upgrade"
                        onClick={() => navigate('/subscription')}
                      >
                        Upgrade Now
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Credit Usage Card */}
            <div className="info-card usage-info">
              <div className="card-header">
                <FaChartBar className="card-icon" />
                <h3>Credit Usage</h3>
              </div>
              <div className="card-content">
                <div className="usage-stats">
                  <div className="stat-item">
                    <div className="stat-label">
                      <FaDatabase className="stat-icon" />
                      <span>Daily Credits</span>
                    </div>
                    <div className="stat-value">{user?.daily_query_limit || 'Loading...'}</div>
                  </div>
                  
                  <div className="usage-progress">
                    <div className="progress-header">
                      <span>Today's Usage</span>
                      <span className="progress-text">
                        {user?.daily_query_usage || 0} / {user?.daily_query_limit || 'Loading...'}
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{
                          width: user?.daily_query_limit ? 
                            `${Math.min((user?.daily_query_usage || 0) / user.daily_query_limit * 100, 100)}%` : 
                            '0%'
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="stat-item">
                    <div className="stat-label">
                      <FaClock className="stat-icon" />
                      <span>Credits Remaining</span>
                    </div>
                    <div className={`stat-value ${user?.queries_remaining === 0 ? 'exceeded' : ''}`}>
                      {user?.queries_remaining || 'Loading...'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profiles Card */}
            <div className="info-card profiles-info">
              <div className="card-header">
                <FaUsers className="card-icon" />
                <h3>Agent Profiles</h3>
              </div>
              <div className="card-content">
                <div className="profiles-stats">
                  <div className="stat-item">
                    <div className="stat-label">
                      <FaDatabase className="stat-icon" />
                      <span>Profile Limit</span>
                    </div>
                    <div className="stat-value">{user?.profile_limit || 'Loading...'}</div>
                  </div>
                  
                  <div className="profiles-progress">
                    <div className="progress-header">
                      <span>Profiles Created</span>
                      <span className="progress-text">
                        {user?.current_profile_count || 0} / {user?.profile_limit || 'Loading...'}
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill profiles-progress-fill"
                        style={{
                          width: user?.profile_limit ? 
                            `${Math.min((user?.current_profile_count || 0) / user.profile_limit * 100, 100)}%` : 
                            '0%'
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="stat-item">
                    <div className="stat-label">
                      <FaCheckCircle className="stat-icon" />
                      <span>Available Slots</span>
                    </div>
                    <div className={`stat-value ${user?.profiles_remaining === 0 ? 'exceeded' : ''}`}>
                      {user?.profiles_remaining || 'Loading...'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Actions Grid */}
        <div className="account-actions-grid">
          {/* Change Username Section */}
          <div className="settings-section action-card">
            <h2><FaUser /> Change Username</h2>
            {!showUsernameForm ? (
              <button className="action-button" onClick={() => setShowUsernameForm(true)}>
                Change Username
              </button>
            ) : (
              <form onSubmit={handleUsernameChange} className="change-form">
                <div className="form-group">
                  <label>New Username:</label>
                  <input
                    type="text"
                    value={usernameForm.newUsername}
                    onChange={(e) => setUsernameForm({...usernameForm, newUsername: e.target.value})}
                    required
                    minLength="3"
                  />
                </div>
                <div className="form-group">
                  <label>Current Password:</label>
                  <input
                    type="password"
                    value={usernameForm.password}
                    onChange={(e) => setUsernameForm({...usernameForm, password: e.target.value})}
                    required
                    placeholder="For verification"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="save-button">
                    <FaSave /> Save
                  </button>
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={() => {
                      setShowUsernameForm(false);
                      setUsernameForm({ newUsername: user?.username || '', password: '' });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Change Email Section */}
          <div className="settings-section action-card">
            <h2><FaEnvelope /> Change Email</h2>
            {!showEmailForm ? (
              <button className="action-button" onClick={() => setShowEmailForm(true)}>
                Change Email
              </button>
            ) : (
              <form onSubmit={handleEmailChange} className="change-form">
                <div className="form-group">
                  <label>New Email:</label>
                  <input
                    type="email"
                    value={emailForm.newEmail}
                    onChange={(e) => setEmailForm({...emailForm, newEmail: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Current Password:</label>
                  <input
                    type="password"
                    value={emailForm.password}
                    onChange={(e) => setEmailForm({...emailForm, password: e.target.value})}
                    required
                    placeholder="For verification"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="save-button">
                    <FaSave /> Save
                  </button>
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={() => {
                      setShowEmailForm(false);
                      setEmailForm({ newEmail: user?.email || '', password: '' });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Change Password Section */}
          <div className="settings-section action-card">
            <h2><FaLock /> Change Password</h2>
            {!showChangePassword ? (
              <button className="action-button" onClick={() => setShowChangePassword(true)}>
                Change Password
              </button>
            ) : (
              <form onSubmit={handlePasswordChange} className="change-form">
                <div className="form-group">
                  <label>Current Password:</label>
                  <div className="password-input">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      required
                    />
                    <button 
                      type="button" 
                      className="password-toggle"
                      onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                    >
                      {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>New Password:</label>
                  <div className="password-input">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      required
                      minLength="8"
                    />
                    <button 
                      type="button" 
                      className="password-toggle"
                      onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                    >
                      {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Confirm Password:</label>
                  <div className="password-input">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      required
                      minLength="8"
                    />
                    <button 
                      type="button" 
                      className="password-toggle"
                      onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                    >
                      {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="save-button">
                    <FaSave /> Update
                  </button>
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={() => {
                      setShowChangePassword(false);
                      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Profiles Management Section */}
        <div className="settings-section">
          <h2>Agent Profiles</h2>
          <p className="section-description">
            Manage your agent connection profiles. Each profile represents a connection to an agent running on an endpoint machine.
          </p>
          {profiles.length === 0 ? (
            <p className="no-profiles">You don't have any profiles yet.</p>
          ) : (
            <div className="profiles-list">
              {profiles.map((profile) => (
                <div key={profile.id} className="profile-item">
                  <div className="profile-info">
                    <h3>{profile.name}</h3>
                    <p>UUID: <code>{profile.uuid}</code></p>
                    <p>Created: {new Date(profile.created_at).toLocaleDateString()}</p>
                  </div>
                  <button 
                    className="delete-profile-button"
                    onClick={() => handleDeleteProfile(profile.uuid)}
                    title="Delete Profile"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Danger Zone */}
        <div className="settings-section danger-zone">
          <h2><FaTrash /> Danger Zone</h2>
          <div className="danger-actions">
            <div className="danger-action">
              <div>
                <h3>Delete Account</h3>
                <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
              </div>
              <button className="danger-button" onClick={handleDeleteAccount}>
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings; 